import React, { useState, useEffect, useCallback } from 'react';
import { VscAdd, VscTrash, VscWarning } from 'react-icons/vsc';
import { getLoadCalc, setBoardFeed, getPowerSources, createPowerSource, updatePowerSource, deletePowerSource } from '../../api/loadCalc';
import { getBreakers, updateBreaker } from '../../api/breakers';
import PrintButton from '../common/PrintButton';
import { buildLoadCalcPdf } from '../../utils/pdfUtils';
import { MODES, SOURCE_KINDS, kw, pct, pctLevel, sourceAvailable, consumerLabel, networkInUse, FED_KINDS } from '../../utils/loadCalc';
import './Reports.css';

const WRITE_ROLES = ['admin', 'engineer', 'manager', 'foreman'];
const PRINT_ROLES = ['admin', 'engineer', 'manager', 'foreman', 'team-leader'];

const VIEWS = [
  { key: 'balance',  label: 'Power balance' },
  { key: 'summary',  label: 'Summary' },
  { key: 'detailed', label: 'Detailed' },
  { key: 'setup',    label: 'Supply setup' },
];

const modeTh = (m) => <th key={m.key} className={`report-num${m.key === 'emergency' ? ' lc-emg' : ''}`}>{m.label}</th>;
const modeTds = (loads, decimals = 1, cls = '') => MODES.map(m => (
  <td key={m.key} className={`report-num${m.key === 'emergency' ? ' lc-emg' : ''} ${cls}`}>{kw(loads?.[m.key], decimals)}</td>
));

// ── Power balance: one card per network ──
function BalanceView({ data }) {
  const networks = data.networks.filter(networkInUse);
  if (!networks.length) return <div className="empty-state">No consumers or sources yet.</div>;
  return networks.map(n => (
    <section key={n.id} className="lc-section">
      <h3 className="lc-title">
        {n.name}{n.voltage ? ` · ${n.voltage}` : ''}
        <span className="lc-sub">{n.boards.length > 1 ? `Boards: ${n.boards.join(', ')}` : 'Single board'}</span>
      </h3>
      <div className="lc-table-wrap">
        <table className="data-table report-table lc-table">
          <thead><tr><th>kW</th><th className="report-num">Installed</th>{MODES.map(modeTh)}</tr></thead>
          <tbody>
            <tr><td>Consumers</td><td className="report-num">{kw(n.consumersInstalled)}</td>{modeTds(n.consumers)}</tr>
            {n.supplies.map((s, i) => (
              <tr key={i}><td className="lc-indent">{s.name}</td><td />{modeTds(s.loads)}</tr>
            ))}
            <tr className="lc-strong"><td>Total power consumption</td><td className="report-num">{kw(n.installed)}</td>{modeTds(n.consumption)}</tr>
            {n.nelRows.length > 0 && (
              <>
                {n.nelRows.map(r => (
                  <tr key={r._id} className="lc-muted-row">
                    <td className="lc-indent">{n.boards.length > 1 ? `${r.board} · ` : ''}{consumerLabel(r)}</td>
                    <td className="report-num">{kw(r.installed)}</td>{modeTds(r.loads)}
                  </tr>
                ))}
                <tr><td>Non-essential loads (NEL trip)</td><td />{modeTds(n.nelLoads)}</tr>
                <tr className="lc-strong"><td>Consumption with NEL trip</td><td />{modeTds(n.afterNel)}</tr>
              </>
            )}
            <tr className="lc-gap"><td colSpan={2 + MODES.length}>Sources</td></tr>
            {n.sources.length === 0 && (
              <tr><td className="lc-indent report-muted" colSpan={2 + MODES.length}>No sources supply this network — add them under Supply setup.</td></tr>
            )}
            {n.sources.map(s => (
              <tr key={s._id}><td className="lc-indent">{s.name}</td><td className="report-num report-muted">{kw(s.available)}</td>{modeTds(s.perMode)}</tr>
            ))}
            <tr className="lc-strong"><td>Available power</td><td />{modeTds(n.available)}</tr>
            <tr>
              <td>Load</td><td />
              {MODES.map(m => <td key={m.key} className={`report-num lc-pct-${pctLevel(n.loadPct[m.key])}`}>{pct(n.loadPct[m.key])}</td>)}
            </tr>
            {n.nelRows.length > 0 && (
              <tr>
                <td>Load with NEL trip</td><td />
                {MODES.map(m => <td key={m.key} className={`report-num lc-pct-${pctLevel(n.loadPctNel[m.key])}`}>{pct(n.loadPctNel[m.key])}</td>)}
              </tr>
            )}
            <tr><td>Spare power</td><td />{modeTds(n.margin)}</tr>
          </tbody>
        </table>
      </div>
    </section>
  ));
}

// ── Summary: one row per switchboard (own consumers) ──
function SummaryView({ data }) {
  return (
    <section className="lc-section">
      <div className="lc-table-wrap">
        <table className="data-table report-table lc-table">
          <thead><tr><th>Switchboard</th><th className="report-num">Consumers</th><th className="report-num">Installed (kW)</th>{MODES.map(modeTh)}</tr></thead>
          <tbody>
            {data.boards.map(b => (
              <tr key={b._id}>
                <td>{b.name}{b.description ? <span className="report-muted"> — {b.description}</span> : null}</td>
                <td className="report-num">{b.own.count}</td>
                <td className="report-num">{kw(b.own.installed)}</td>
                {modeTds(b.own.loads)}
              </tr>
            ))}
            <tr className="lc-strong">
              <td>Total power consumption</td>
              <td className="report-num">{data.totals.count}</td>
              <td className="report-num">{kw(data.totals.installed)}</td>
              {modeTds(data.totals.loads)}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="report-muted lc-note">Consumers only — transformer losses are included in the power balance.</p>
    </section>
  );
}

// ── Detailed: every consumer per switchboard ──
function DetailedView({ data }) {
  return data.boards.map(b => (
    <section key={b._id} className="lc-section">
      <h3 className="lc-title">
        {b.name}{b.description ? ` — ${b.description}` : ''}
        <span className="lc-sub">{[b.voltage, b.fedFrom && `fed from ${b.fedFrom.tag} (${b.fedFrom.board})`].filter(Boolean).join(' · ')}</span>
      </h3>
      <div className="lc-table-wrap">
        <table className="data-table report-table lc-table">
          <thead>
            <tr><th>Breaker</th><th>Consumer</th><th>Location</th><th className="report-num">kW</th><th>NEL</th>{MODES.map(modeTh)}</tr>
          </thead>
          <tbody>
            {b.rows.map(r => (
              <tr key={r._id} className={r.replaced ? 'lc-replaced' : ''} title={r.replaced ? 'Feeds a switchboard / transformer — replaced by the calculated supply' : undefined}>
                <td className="report-breaker">{r.breaker}</td>
                <td>{consumerLabel(r)}{r.replaced && <span className="lc-replaced-note"> — see calculated supply</span>}</td>
                <td className="report-muted">{r.location}</td>
                <td className="report-num">{kw(r.installed, 2)}</td>
                <td>{r.nelTrip ? 'Yes' : ''}</td>
                {modeTds(r.loads, 2)}
              </tr>
            ))}
            {b.feeds.map((f, i) => (
              <tr key={`f${i}`} className="lc-feed-row">
                <td className="report-breaker">{f.breaker}</td>
                <td colSpan={2}>{f.description}</td>
                <td className="report-num">{kw(f.installed, 2)}</td>
                <td />
                {modeTds(f.loads, 2)}
              </tr>
            ))}
            {b.rows.length === 0 && b.feeds.length === 0 && (
              <tr><td colSpan={5 + MODES.length} className="report-muted">No consumers on this switchboard.</td></tr>
            )}
            <tr className="lc-strong">
              <td colSpan={3}>Totals for {b.name} ({b.own.count} consumer{b.own.count === 1 ? '' : 's'})</td>
              <td className="report-num">{kw(b.own.installed)}</td><td />
              {modeTds(b.own.loads)}
            </tr>
            {b.feeds.length > 0 && (
              <tr className="lc-strong">
                <td colSpan={3}>Incl. supplied boards</td>
                <td className="report-num">{kw(b.total.installed)}</td><td />
                {modeTds(b.total.loads)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  ));
}

// ── Setup: board feeds + power sources ──
const toNum = (v) => (v === '' || v == null ? '' : Number(String(v).replace(',', '.')));

// Breakers grouped per board; `only` limits it to one board's breakers, `exclude` leaves one board out
function BreakerSelect({ breakers, value, onChange, exclude, only, emptyLabel, disabled }) {
  const groups = new Map();
  for (const b of breakers) {
    const board = b.switchboard?.name || '?';
    if (exclude && String(b.switchboard?._id) === String(exclude)) continue;
    if (only !== undefined && String(b.switchboard?._id) !== String(only)) continue;
    if (!groups.has(board)) groups.set(board, []);
    groups.get(board).push(b);
  }
  const sorted = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  return (
    <select className="lc-input" value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled}>
      <option value="">{emptyLabel}</option>
      {sorted.map(([board, list]) => (
        <optgroup key={board} label={board}>
          {list.sort((a, b) => a.tag.localeCompare(b.tag, undefined, { numeric: true })).map(b => <option key={b._id} value={b._id}>{b.tag}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function NumCell({ value, onSave, disabled, width = 70 }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => {
    const n = toNum(v);
    if (n !== '' && Number.isNaN(n)) { setV(value ?? ''); return; }
    if ((n === '' ? null : n) !== (value ?? null)) onSave(n);
  };
  return (
    <input className="lc-input report-num" style={{ width }} value={v} disabled={disabled}
      onChange={e => setV(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setV(value ?? ''); } }} />
  );
}

function TextCell({ value, onSave, disabled, placeholder, width }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  return (
    <input className="lc-input" style={width ? { width } : undefined} value={v} placeholder={placeholder} disabled={disabled}
      onChange={e => setV(e.target.value)} onBlur={() => { if (v.trim() !== (value ?? '')) onSave(v.trim()); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setV(value ?? ''); }} />
  );
}

function SetupView({ project, data, canEdit, onChanged }) {
  const [breakers, setBreakers] = useState([]);
  const [sources, setSources]   = useState([]);
  const [error, setError]       = useState('');

  const loadSources = useCallback(() => getPowerSources(project._id).then(r => setSources(r.data)).catch(() => {}), [project._id]);
  useEffect(() => {
    getBreakers(project._id).then(r => setBreakers(r.data)).catch(() => {});
    loadSources();
  }, [project._id, loadSources]);

  const fail = (e) => setError(e?.response?.data?.msg || 'Could not save.');

  const changeFeed = async (boardId, patch) => {
    setError('');
    try { await setBoardFeed(boardId, patch); onChanged(); } catch (e) { fail(e); }
  };

  // ── Bus ties: stored on the breaker (its own section ↔ tieBoard / tieSection) ──
  const [newTie, setNewTie] = useState({ board: '', breaker: '' });
  const boardById = new Map(data.boards.map(b => [String(b._id), b]));
  const ties = breakers.filter(b => b.tieBoard).sort((a, b) =>
    (a.switchboard?.name || '').localeCompare(b.switchboard?.name || '', undefined, { numeric: true }) || a.tag.localeCompare(b.tag, undefined, { numeric: true }));

  const saveBreaker = async (brk, patch) => {
    setError('');
    setBreakers(prev => prev.map(b => (b._id === brk._id ? { ...b, ...patch } : b)));
    try { await updateBreaker(brk._id, patch); onChanged(); } catch (e) { fail(e); getBreakers(project._id).then(r => setBreakers(r.data)).catch(() => {}); }
  };

  const addTie = async () => {
    const brk = breakers.find(b => b._id === newTie.breaker);
    if (!brk) return;
    const board = boardById.get(String(brk.switchboard?._id));
    const other = (board?.sections || []).find(s => s !== brk.section) || '';
    await saveBreaker(brk, { tieBoard: brk.switchboard?._id, tieSection: other });
    setNewTie({ board: '', breaker: '' });
  };

  const sectionOptions = (boardId, value) => {
    const list = boardById.get(String(boardId))?.sections || [];
    return (
      <>
        <option value="">{list.length ? '—' : 'Busbar'}</option>
        {list.map(s => <option key={s} value={s}>{s}</option>)}
        {value && !list.includes(value) && <option value={value}>{value} (removed)</option>}
      </>
    );
  };

  const saveSource = async (s, patch) => {
    setError('');
    setSources(prev => prev.map(x => (x._id === s._id ? { ...x, ...patch } : x)));
    try { await updatePowerSource(s._id, patch); onChanged(); } catch (e) { fail(e); loadSources(); }
  };

  const addSource = async () => {
    setError('');
    try {
      const order = sources.reduce((m, s) => Math.max(m, s.order || 0), 0) + 1;
      const r = await createPowerSource({ project: project._id, name: `Generator ${sources.filter(s => s.kind === 'generator').length + 1}`, kind: 'generator', order });
      setSources(prev => [...prev, r.data]);
      onChanged();
    } catch (e) { fail(e); }
  };

  const removeSource = async (s) => {
    if (!confirm(`Delete source "${s.name}"?`)) return;
    try { await deletePowerSource(s._id); setSources(prev => prev.filter(x => x._id !== s._id)); onChanged(); } catch (e) { fail(e); }
  };

  return (
    <>
      {error && <div className="lc-error">{error}</div>}
      <section className="lc-section">
        <h3 className="lc-title">Switchboard feeds</h3>
        <p className="report-muted lc-note">
          Pick the breaker that feeds each board directly — the board then counts as a load on that breaker, and both
          boards share the same sources — and the board's own incoming breaker. Boards fed through a transformer or UPS stay
          empty here; add the transformer as a source below.
        </p>
        <div className="lc-table-wrap">
          <table className="data-table report-table lc-table lc-setup">
            <thead><tr><th>Switchboard</th><th>Voltage</th><th>Fed from</th><th>Incoming breaker</th><th>Network</th></tr></thead>
            <tbody>
              {data.boards.map(b => (
                <tr key={b._id}>
                  <td>{b.name}{b.description ? <span className="report-muted"> — {b.description}</span> : null}</td>
                  <td>{b.voltage}</td>
                  <td>
                    <BreakerSelect breakers={breakers} value={b.fedFrom?._id} exclude={b._id} disabled={!canEdit}
                      emptyLabel="— Own sources / transformer —" onChange={v => changeFeed(b._id, { fedFrom: v || null })} />
                  </td>
                  <td>
                    {b.fedFrom && (
                      <BreakerSelect breakers={breakers} value={b.incomingBreaker} only={b._id} disabled={!canEdit}
                        emptyLabel="—" onChange={v => changeFeed(b._id, { incomingBreaker: v || null })} />
                    )}
                  </td>
                  <td className="report-muted">{data.boards.find(x => String(x._id) === b.network)?.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="lc-section">
        <h3 className="lc-title">
          Power sources
          {canEdit && <button className="btn-secondary lc-add" onClick={addSource}><VscAdd size={13} /> Add source</button>}
        </h3>
        <p className="report-muted lc-note">
          Available power = kW, or kVA × cos φ when kW is empty. <b>Incoming breaker</b> is the source's breaker on the board it
          supplies. Transformers and UPS: set <b>Fed from</b> to the breaker on the primary side — they count as a load there
          (secondary consumption ÷ efficiency). Tick the modes each source is in service.
        </p>
        {sources.length === 0 ? (
          <div className="empty-state">No sources yet.</div>
        ) : (
          <div className="lc-table-wrap">
            <table className="data-table report-table lc-table lc-setup">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>Supplies</th><th>Incoming breaker</th><th>Fed from</th>
                  <th className="report-num">kVA</th><th className="report-num">cos φ</th><th className="report-num">kW</th>
                  <th className="report-num">Eff. %</th><th>Voltage</th><th className="report-num">A</th>
                  {MODES.map(m => <th key={m.key} className={`lc-mode-th${m.key === 'emergency' ? ' lc-emg' : ''}`} title={m.label}>{m.label}</th>)}
                  <th className="report-num lc-avail">Available</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {sources.map(s => (
                  <tr key={s._id}>
                    <td><TextCell value={s.name} disabled={!canEdit} width={170} onSave={v => v && saveSource(s, { name: v })} /></td>
                    <td>
                      <select className="lc-input" value={s.kind} disabled={!canEdit} onChange={e => saveSource(s, FED_KINDS.includes(e.target.value) ? { kind: e.target.value } : { kind: e.target.value, fedFrom: null })}>
                        {SOURCE_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="lc-input" value={s.switchboard || ''} disabled={!canEdit} onChange={e => saveSource(s, { switchboard: e.target.value || null, incomingBreaker: null })}>
                        <option value="">— Board —</option>
                        {data.boards.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                      </select>
                    </td>
                    <td>
                      {s.switchboard && (
                        <BreakerSelect breakers={breakers} value={s.incomingBreaker} only={s.switchboard} disabled={!canEdit}
                          emptyLabel="—" onChange={v => saveSource(s, { incomingBreaker: v || null })} />
                      )}
                    </td>
                    <td>
                      {FED_KINDS.includes(s.kind) && (
                        <BreakerSelect breakers={breakers} value={s.fedFrom} disabled={!canEdit} exclude={s.switchboard}
                          emptyLabel="—" onChange={v => saveSource(s, { fedFrom: v || null })} />
                      )}
                    </td>
                    <td><NumCell value={s.kva} disabled={!canEdit} onSave={v => saveSource(s, { kva: v })} /></td>
                    <td><NumCell value={s.cosPhi} disabled={!canEdit} width={56} onSave={v => saveSource(s, { cosPhi: v })} /></td>
                    <td><NumCell value={s.kw} disabled={!canEdit} onSave={v => saveSource(s, { kw: v })} /></td>
                    <td><NumCell value={s.efficiency} disabled={!canEdit} width={56} onSave={v => saveSource(s, { efficiency: v })} /></td>
                    <td><TextCell value={s.voltage} disabled={!canEdit} width={70} onSave={v => saveSource(s, { voltage: v })} /></td>
                    <td><NumCell value={s.current_a} disabled={!canEdit} width={60} onSave={v => saveSource(s, { current_a: v })} /></td>
                    {MODES.map(m => (
                      <td key={m.key} className="lc-mode-cell">
                        <input type="checkbox" checked={!!s.modes?.[m.key]} disabled={!canEdit}
                          onChange={e => saveSource(s, { modes: { ...s.modes, [m.key]: e.target.checked } })} />
                      </td>
                    ))}
                    <td className="report-num">{kw(sourceAvailable(s))}</td>
                    {canEdit && <td><button className="btn-icon" title="Delete source" onClick={() => removeSource(s)}><VscTrash size={13} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="lc-section">
        <h3 className="lc-title">Bus-tie breakers</h3>
        <p className="report-muted lc-note">
          A bus tie joins a busbar section to another section of the same board, or to another switchboard. Busbar sections
          are set on the switchboard under Reference Tables → Switchboards &amp; Breakers (e.g. "A, B"). The load calculation
          treats bus ties as closed.
        </p>
        {ties.length > 0 && (
          <div className="lc-table-wrap">
            <table className="data-table report-table lc-table lc-setup">
              <thead><tr><th>Switchboard</th><th>Breaker</th><th>Section</th><th>Ties to board</th><th>Section</th>{canEdit && <th />}</tr></thead>
              <tbody>
                {ties.map(t => {
                  const own = String(t.switchboard?._id);
                  const same = String(t.tieBoard) === own && (t.tieSection || '') === (t.section || '');
                  return (
                    <tr key={t._id}>
                      <td>{t.switchboard?.name}</td>
                      <td className="report-breaker">{t.tag}</td>
                      <td>
                        <select className="lc-input" value={t.section || ''} disabled={!canEdit} onChange={e => saveBreaker(t, { section: e.target.value })}>
                          {sectionOptions(own, t.section)}
                        </select>
                      </td>
                      <td>
                        <select className="lc-input" value={String(t.tieBoard)} disabled={!canEdit} onChange={e => saveBreaker(t, { tieBoard: e.target.value, tieSection: '' })}>
                          {data.boards.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className={`lc-input${same ? ' lc-input-bad' : ''}`} value={t.tieSection || ''} disabled={!canEdit}
                          title={same ? 'A bus tie must join two different sections' : undefined}
                          onChange={e => saveBreaker(t, { tieSection: e.target.value })}>
                          {sectionOptions(t.tieBoard, t.tieSection)}
                        </select>
                      </td>
                      {canEdit && (
                        <td><button className="btn-icon" title="Not a bus tie" onClick={() => saveBreaker(t, { tieBoard: null, tieSection: null })}><VscTrash size={13} /></button></td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {canEdit && (
          <div className="lc-add-row">
            <select className="lc-input" value={newTie.board} onChange={e => setNewTie({ board: e.target.value, breaker: '' })}>
              <option value="">— Switchboard —</option>
              {data.boards.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
            {newTie.board && (
              <BreakerSelect breakers={breakers.filter(b => !b.tieBoard)} value={newTie.breaker} only={newTie.board}
                emptyLabel="— Breaker —" onChange={v => setNewTie(p => ({ ...p, breaker: v }))} />
            )}
            <button className="btn-secondary lc-add-inline" disabled={!newTie.breaker} onClick={addTie}><VscAdd size={13} /> Add bus tie</button>
          </div>
        )}
        {!canEdit && ties.length === 0 && <div className="empty-state">No bus ties.</div>}
      </section>
    </>
  );
}

function LoadCalculation({ project, role, onOpenTab }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [view, setView]   = useState(() => {
    try { return localStorage.getItem('loadcalc_view') || 'balance'; } catch { return 'balance'; }
  });
  const canEdit = WRITE_ROLES.includes(role);

  const load = useCallback(() => {
    if (!project?._id) return;
    getLoadCalc(project._id)
      .then(r => { setData(r.data); setError(''); })
      .catch(e => setError(e?.response?.data?.msg || 'Could not load the calculation.'));
  }, [project?._id]);
  useEffect(() => { load(); }, [load]);

  const choose = (v) => { setView(v); try { localStorage.setItem('loadcalc_view', v); } catch {} };

  const printView = view === 'setup' ? null : view;
  const viewLabel = VIEWS.find(v => v.key === view)?.label;
  const title = `Electrical load calculation — ${viewLabel} — ${project?.name ?? ''}`;

  return (
    <div className="module-wrap">
      <div className="module-toolbar report-toolbar">
        <span className="module-toolbar-title">Load Calculation</span>
        <div className="lc-views">
          {VIEWS.map(v => (
            <button key={v.key} className={`lc-view-btn${view === v.key ? ' active' : ''}`} onClick={() => choose(v.key)}>{v.label}</button>
          ))}
        </div>
        {data?.warnings?.length > 0 && (
          <span className="lc-warn" title={data.warnings.join('\n')}><VscWarning size={14} /> {data.warnings.length}</span>
        )}
        {PRINT_ROLES.includes(role) && data && printView && (
          <PrintButton
            title={title}
            storageKey={`load-calc-${printView}`}
            rows={[]}
            onOpenTab={onOpenTab}
            columns={[
              ...MODES.map(m => ({ key: m.key, label: m.label })),
              ...(printView === 'detailed' ? [{ key: 'location', label: 'Location' }, { key: 'nel', label: 'NEL trip' }] : []),
            ]}
            chartPdf={(keys) => buildLoadCalcPdf(printView, data, project, keys)}
          />
        )}
      </div>
      <div className="module-body report-body lc-body">
        {error && <div className="lc-error">{error}</div>}
        {data?.warnings?.length > 0 && (
          <ul className="lc-warnings">{data.warnings.map((w, i) => <li key={i}><VscWarning size={12} /> {w}</li>)}</ul>
        )}
        {!data && !error && <div className="empty-state">Loading…</div>}
        {data && view === 'balance'  && <BalanceView data={data} />}
        {data && view === 'summary'  && <SummaryView data={data} />}
        {data && view === 'detailed' && <DetailedView data={data} />}
        {data && view === 'setup'    && <SetupView project={project} data={data} canEdit={canEdit} onChanged={load} />}
      </div>
    </div>
  );
}

export default LoadCalculation;
